import { useEffect, useRef } from 'react';
import { useToast } from '@/context/ToastContext';
import { fetchQuietSchedule } from '@/services/outreach';
import type { OutreachRecord } from '@/types/outreach';

interface Options {
  waiting: OutreachRecord[];
  /** The list has finished its first load. */
  ready: boolean;
  /** New Members is switched on. */
  enabled: boolean;
}

/** Wording for the toast. Names are fine in the app; only lock-screen pushes stay generic. */
export function newMemberToastMessage(fresh: OutreachRecord[]): string {
  if (fresh.length === 1)
    return `${fresh[0].displayName} just joined. Open Needs a look to say hello.`;
  return `${fresh.length} new members just joined. Open Needs a look to say hello.`;
}

/**
 * A gentle in-app heads-up when someone new joins while a leader has the app open.
 * People already waiting when the app opens do not trigger it, and nothing shows
 * during Shabbat or Yom Tov quiet time.
 */
export function useNewMemberToast({ waiting, ready, enabled }: Options): void {
  const { showToast } = useToast();
  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!enabled || !ready) return;
    if (seen.current === null) {
      seen.current = new Set(waiting.map((r) => r.memberId));
      return;
    }
    const known = seen.current;
    const fresh = waiting.filter((r) => !known.has(r.memberId));
    if (fresh.length === 0) return;
    fresh.forEach((r) => known.add(r.memberId));

    let cancelled = false;
    fetchQuietSchedule(undefined, 1)
      .then((res) => !res.now.quiet)
      .catch(() => true)
      .then((allowed) => {
        if (!cancelled && allowed) {
          showToast({ message: newMemberToastMessage(fresh), type: 'info', duration: 6000 });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [waiting, ready, enabled, showToast]);
}
